import "./reset.scss";
import "./App.scss";
import { Routes, Route } from "react-router-dom";
import Home from "./pages/Home";
import Login from "./components/forms/Login";
import ToastContainer from "./components/toast/ToastContainer";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import SubscriptionForm from "./components/forms/formSteps/SubscriptionForm.jsx";
import AdminVideoList from "./components/admin/adminVideos/AdminVideoList.jsx";
import AdminCategoryList from "./components/admin/categories/CategoryList.jsx";
import AdminAddEditCategory from "./components/admin/categories/AdminAddEditCategory.jsx";
import Membership from "./components/membership/Membership.jsx";
import UsersList from "./components/admin/users/UsersList.jsx";
import AdminFaqList from "./components/admin/adminFaq/FaqList.jsx";
import AdminAddEditFaq from "./components/admin/adminFaq/AdminAddEditFaq.jsx";
import FaqList from "./features/faqs/FaqList.jsx";
import AdminAboutList from "./components/admin/adminAbout/AdminAboutList.jsx";
import AdminAddEditAbout from "./components/admin/adminAbout/AdminAddEditAbout.jsx";
import AuthProvider from "./features/auth/AuthProvider.jsx";
import AddEditProduct from "./components/admin/products/AddEditProduct.jsx";
import GeneralLayout from "./components/layout/GeneralLayout.jsx";
import DashboardLayout from "./components/layout/DashLayout.jsx";
import AdminDashboard from "./components/admin/AdminDashboard.jsx";
import RequireAuth from "./features/auth/RequireAuth.jsx";
import ProductsList from "./components/admin/products/ProductsList.jsx";
import AddVideo from "./components/admin/adminVideos/AddVideo.jsx";
import EditVideo from "./components/admin/adminVideos/EditVideo.jsx";
import MyList from "./pages/MyList.jsx";
import WatchPage from "./pages/WatchPage.jsx";
import UserList from "./components/admin/users/UsersList.jsx";
import useAuthInitializer from "./hooks/useAuthInitializer.js";
import LoadingSpinner from "./components/loader/Loader";
import { initGuestSession } from "./features/auth/guestSlice.js";
import SubscriptionList from "./components/admin/subscriptions/SubscriptionList.jsx";
import SubscriptionDetail from "./components/admin/subscriptions/SubscriptionDetail.jsx";
import AdminAlerts from "./components/admin/alerts/AdminAlerts.jsx";
import TransactionsTable from "./components/admin/TransactionsTable/TransactionsTable.jsx";
import OtpLoginPage from "./components/forms/OtpLoginPage.jsx";
import ForgotPassword from "./components/forms/ForgotPassword.jsx";
import ResetPassword from "./components/forms/ResetPassword.jsx";
import AccountDashboard from "./components/admin/users/AccountDashboard.jsx";
import ActivityLog from "./components/admin/activityLog/ActivityLog.jsx";
import WooProductsPage from "./pages/WooProductsPage.jsx";
import ContactPage from "./pages/ContactPage.jsx";
import AboutPage from "./pages/AboutPage.jsx";
import WooProductsList from "./components/admin/products/WooProducts.jsx";
import { VideoUploadProvider } from "./components/uploads/VideoUploadContext.jsx";
import Profile from "./components/profile/Profile.jsx";
import AdminAddPlan from "./components/admin/plans/AdminAddPlan.jsx";
import AdminPlansList from "./components/admin/plans/AdminPlansList.jsx";
import PdfGridPage from "./components/plans/PdfGridPage.jsx";

import { pdfjs } from "react-pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import AdminFaq from "./components/admin/adminFaq/AdminFaq.jsx";
import RegisterAdmin from "./components/forms/RegisterAdmin.jsx";
import AdminInvite from "./components/forms/AdminInvite.jsx";

function App() {
  const dispatch = useDispatch();
  const { isLoaderOpen } = useSelector((state) => state.videoSlice);

  // ✅ Load worker from CDN
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;
  // Ensure guest session is initialized once
  useEffect(() => {
    dispatch(initGuestSession());
  }, [dispatch]);

  const isReady = useAuthInitializer();
  if (!isReady) return <LoadingSpinner />;

  return (
    <>
      {isLoaderOpen && <LoadingSpinner />}
      <ToastContainer />
      <div className="site-wrappers">
        <main className="site-contents">
          <Routes>
            <Route element={<AuthProvider />}>
              <Route path="/" element={<GeneralLayout />}>
                <Route index element={<Home />} />
                <Route path="/register/admin" element={<RegisterAdmin />} />
                <Route path="forgotpassword" element={<ForgotPassword />} />
                <Route
                  path="/resetpassword/:resetToken"
                  element={<ResetPassword />}
                />
                <Route path="otplogin" element={<OtpLoginPage />} />
                <Route path="member" element={<SubscriptionForm />} />
                <Route path="login" element={<Login />} />
                <Route path="subscribe" element={<Membership />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="products" element={<WooProductsPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="faq" element={<FaqList />} />
                <Route path="plans" element={<PdfGridPage />} />
                <Route path="watch/:idOrSlug" element={<WatchPage />} />

                <Route
                  element={<RequireAuth allowedRoles={["Admin", "User"]} />}
                >
                  <Route path="my-list" element={<MyList />} />
                  <Route path="user/dashboard" element={<AccountDashboard />} />
                  <Route path="profile" element={<Profile />} />
                </Route>

                {/* Start Admin */}
                <Route element={<RequireAuth allowedRoles={["Admin"]} />}>
                  <Route path="admin" element={<DashboardLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="subscribers">
                      <Route index element={<UsersList />} />
                    </Route>
                    <Route path="invite" element={<AdminInvite />} />
                    <Route path="transactions">
                      <Route index element={<TransactionsTable />} />
                    </Route>
                    <Route path="subscriptions">
                      <Route index element={<SubscriptionList />} />
                      <Route
                        path="details/:id"
                        element={<SubscriptionDetail />}
                      />
                    </Route>
                    <Route path="alerts">
                      <Route index element={<AdminAlerts />} />
                    </Route>

                    <Route path="plans">
                      <Route index element={<AdminPlansList />} />
                      <Route
                        path="add-plan"
                        element={
                          <VideoUploadProvider>
                            <AdminAddPlan />
                          </VideoUploadProvider>
                        }
                      />
                      {/* <Route path="edit-video/:id" element={<EditVideo />} /> */}
                    </Route>

                    <Route path="videos">
                      <Route index element={<AdminVideoList />} />
                      <Route
                        path="add-video"
                        element={
                          <VideoUploadProvider>
                            <AddVideo />
                          </VideoUploadProvider>
                        }
                      />
                      <Route
                        path="edit-video/:id"
                        element={
                          <VideoUploadProvider>
                            <EditVideo />
                          </VideoUploadProvider>
                        }
                      />
                    </Route>
                    <Route path="categories">
                      <Route index element={<AdminCategoryList />} />
                      <Route
                        path="add-category"
                        element={<AdminAddEditCategory />}
                      />
                      <Route
                        path="edit-category/:id"
                        element={<AdminAddEditCategory />}
                      />
                    </Route>
                    <Route path="products">
                      <Route index element={<ProductsList />} />
                      <Route
                        path="woo-products"
                        element={<WooProductsList />}
                      />
                      <Route path="add-product" element={<AddEditProduct />} />
                      <Route
                        path="edit-product/:id"
                        element={<AddEditProduct />}
                      />
                    </Route>
                    <Route path="activities">
                      <Route index element={<ActivityLog />} />
                    </Route>
                    <Route path="users">
                      <Route index element={<UserList />} />
                      {/* <Route path="edit-user" element={<AddEditProduct />} /> */}
                    </Route>

                    <Route path="about">
                      <Route index element={<AdminAboutList />} />
                      <Route path="addabout" element={<AdminAddEditAbout />} />
                      <Route
                        path="editabout/:id"
                        element={<AdminAddEditAbout />}
                      />
                    </Route>

                    <Route path="faqs">
                      <Route index element={<AdminFaqList />} />
                      <Route path="addfaq" element={<AdminAddEditFaq />} />
                      <Route path="editfaq/:id" element={<AdminAddEditFaq />} />
                    </Route>
                  </Route>
                </Route>
                {/* End Admin */}
              </Route>
            </Route>
          </Routes>
        </main>
      </div>
    </>
  );
}

export default App;
